/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */

import { noChange } from 'lit';
import { directive, Directive, PartInfo, PartType } from 'lit/directive.js';
import Mustache from 'mustache'

class MustacheTemplateContentDirective extends Directive {
  private _previousIdentifier?: string;

  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.CHILD) {
      throw new Error('templateContent can only be used in child bindings');
    }
  }

  render(template: HTMLTemplateElement, data: any = {}, identifier: string | undefined = undefined) {
    if (this._previousIdentifier === identifier && identifier !== undefined) {
      return noChange;
    }
    this._previousIdentifier = identifier;
    const importedDocument = document.importNode(template.content, true);
    // use mustache.js to enter variables
    //debugger;
    for(let child of importedDocument.children) {
      // Render Inner HTML
      const mustacheResult = Mustache.render(child.innerHTML, data)
      if (child.innerHTML !== mustacheResult) {
        child.innerHTML = mustacheResult;
      }
      // Render to attributes
      for (let attr of child.attributes) {
        // If attribute has mustache-able templates
        if (this.TestAttributeHasTemplate(attr.value)) {
          attr.value = Mustache.render(attr.value, data);
        }
      }
    }
    return importedDocument;
  }

  private TestAttributeHasTemplate(value: string): boolean {
    try {
      const spans = Mustache.parse(value);
      return spans.some(([span, ..._]) => span === 'name')
    }
    catch {
      return false;
    }
  }
}

/**
 * Renders the content of a template element as HTML.
 *
 * Note, the template should be developer controlled and not user controlled.
 * Rendering a user-controlled template with this directive
 * could lead to cross-site-scripting vulnerabilities.
 */
export const mustacheTemplateContent = directive(MustacheTemplateContentDirective);

/**
 * The type of the class that powers this directive. Necessary for naming the
 * directive's return type.
 */
export type {MustacheTemplateContentDirective};
